import { Box, Card, CardContent, LinearProgress, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

export const formatReportDateRangeLabel = (startDate, endDate) => {
  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const start = formatDate(startDate);
  const end = formatDate(endDate);

  if (start && end) return `${start} - ${end}`;
  return start || end || '';
};

export const GlassLoadingState = ({ icon, title, subtitle }) => {
  const theme = useTheme();
  const primaryMain = theme.palette?.primary?.main || '#1976d2';
  const primaryLight = theme.palette?.primary?.light || primaryMain;
  const secondaryMain = theme.palette?.secondary?.main || primaryMain;
  const paper = theme.palette?.background?.paper || '#ffffff';
  const white = theme.palette?.common?.white || '#ffffff';

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        display: 'grid',
        placeItems: 'center',
        minHeight: { xs: 240, md: 300 },
        px: { xs: 0.5, sm: 1 },
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 620,
          borderRadius: 4,
          border: `1px solid ${alpha(primaryMain, 0.16)}`,
          background: `linear-gradient(145deg, ${alpha(paper, 0.92)} 0%, ${alpha(primaryLight, 0.1)} 100%)`,
          backdropFilter: 'blur(22px)',
          boxShadow: `0 24px 60px ${alpha(primaryMain, 0.14)}`,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'grid', gap: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color: primaryMain,
                background: `radial-gradient(circle at 30% 30%, ${alpha(primaryLight, 0.38)} 0%, ${alpha(primaryMain, 0.12)} 58%, ${alpha(secondaryMain, 0.16)} 100%)`,
                border: `1px solid ${alpha(primaryMain, 0.18)}`,
                boxShadow: `inset 0 1px 0 ${alpha(white, 0.32)}, 0 14px 30px ${alpha(primaryMain, 0.14)}`,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 420 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <LinearProgress
              sx={{
                height: 10,
                borderRadius: 999,
                backgroundColor: alpha(primaryMain, 0.08),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${primaryMain} 0%, ${secondaryMain} 100%)`,
                },
              }}
            />

            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: { xs: '1fr', sm: '1.25fr 0.75fr' },
              }}
            >
              {[0, 1].map((item) => (
                <Box
                  key={item}
                  sx={{
                    height: 12,
                    borderRadius: 999,
                    backgroundColor: alpha(primaryMain, item === 0 ? 0.1 : 0.06),
                  }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
